import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from '../components/BreadCrumbs';
import { getAIUserMessageHistory } from '../services/aiChat';
import { formatDateTime } from '../utils/FormatDateTime';
import { BiArrowBack } from 'react-icons/bi';
import { MessageCircle, Bot, Send, Calendar, Clock } from 'lucide-react';

const UserChatHistory = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        fetchChatHistory();
    }, [page, userId]);

    const fetchChatHistory = async () => {
        try {
            setLoading(true);
            const response = await getAIUserMessageHistory(userId, page);
            // Reverse messages for standard chat view if they come in reverse chronological order
            // APIs often return latest first for pagination, but chat UI needs chronological
            const sortedMessages = [...response.data.messages].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

            if (page === 1) {
                setMessages(sortedMessages);
            } else {
                setMessages(prev => [...sortedMessages, ...prev]);
            }
            setTotalPages(response.data.total_pages);
        } catch (error) {
            console.error('Error fetching chat history:', error);
            toast.error('Failed to fetch chat history');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/ai-user-chats');
    };

    return (
        <div className="p-6 h-[calc(100vh-100px)] flex flex-col">
            <div className="mb-6 shrink-0">
                <Breadcrumbs
                    items={[
                        { name: 'Home', link: '/dashboard' },
                        { name: 'AI User Chats', link: '/ai-user-chats' },
                        { name: 'History' }
                    ]}
                />
                <div className="flex items-center gap-4 mt-3">
                    <button
                        onClick={handleBack}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <BiArrowBack className="w-6 h-6 text-gray-600" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Chat History</h1>
                        <p className="text-sm text-gray-500">User ID: {userId}</p>
                    </div>
                </div>
            </div>

            <div className="flex-1 bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden flex flex-col mb-4">
                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50/50 space-y-6">
                    {loading && page === 1 ? (
                        <div className="flex justify-center items-center h-full">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                        </div>
                    ) : (
                        <>
                            {totalPages > page && (
                                <button
                                    onClick={() => setPage(page + 1)}
                                    className="w-full text-center py-2 text-sm text-primary font-medium hover:underline"
                                >
                                    Load previous messages
                                </button>
                            )}

                            {messages.map((msg) => (
                                <div key={msg.id} className="space-y-4">
                                    {/* User Message */}
                                    <div className="flex justify-end">
                                        <div className="max-w-[80%] space-y-1">
                                            <div className="bg-primary text-white p-4 rounded-2xl rounded-tr-none shadow-md">
                                                <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                                            </div>
                                            <div className="flex items-center justify-end gap-2 text-[10px] text-gray-400">
                                                <Clock className="w-3 h-3" />
                                                <span>{formatDateTime(msg.timestamp)}</span>
                                                {msg.intent && (
                                                    <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                                                        {msg.intent.toUpperCase()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* AI Response */}
                                    <div className="flex justify-start">
                                        <div className="flex gap-3 max-w-[85%]">
                                            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 border border-emerald-200">
                                                <Bot className="w-5 h-5 text-emerald-600" />
                                            </div>
                                            <div className="space-y-1">
                                                <div className="bg-white border border-gray-100 p-4 rounded-2xl rounded-tl-none shadow-sm prose prose-sm max-w-none">
                                                    <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                                                        {msg.response}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                                    <Clock className="w-3 h-3" />
                                                    <span>{formatDateTime(msg.timestamp)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserChatHistory;
